import { useState } from 'react'

import { useAuth } from '../../contexts/AuthContext'

import { CONFIG } from '../../config'
import { ApiError } from '../../types/exception/errorStatus'
import { getErrorMessage } from '../../utils/exception/exceptionUtils'
import { useTranslation } from 'react-i18next'

type RefreshResponse = { accessToken: string; refreshToken?: string }

export function useRefresh() {
  const { t } = useTranslation('exceptions')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { authInfo, saveAuthInfo, clearAuthInfo } = useAuth()

  const resetState = () => {
    setLoading(false)
    setError(null)
  }

  const refresh = async (): Promise<string | null> => {
    if (!authInfo?.refreshToken) {
      setError(null)
      return null
    }

    setLoading(true)
    try {
      const response = await fetch(`${CONFIG.BOUNCE_API}/api/v1/auth/token/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: authInfo.refreshToken }),
      })

      if (!response.ok) {
        clearAuthInfo()
        window.location.href = '/login'
        return null
      }

      const data: RefreshResponse = await response.json()

      if (data?.accessToken) {
        saveAuthInfo({
          ...authInfo,
          accessToken: data.accessToken,
          refreshToken: data.refreshToken || authInfo.refreshToken,
        })
        setError(null)
        return data.accessToken
      } else {
        clearAuthInfo()
        window.location.href = '/login'
        return null
      }
    } catch (err) {
      console.error(err)
      clearAuthInfo()
      if (err instanceof ApiError) {
        const translatedMessage = getErrorMessage(err.data)
        setError(translatedMessage)
      } else setError(t('exceptions:unknown'))
      window.location.href = '/login'
      return null
    } finally {
      setLoading(false)
    }
  }

  return { refresh, loading, error, resetState }
}
